import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { BLOG_POSTS, mapDbPostToUi } from "./blogData";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";

function renderInline(text = "") {
  const parts = text
    .split(/(\[[^\]]+\]\([^\)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g)
    .filter(Boolean);

  return parts.map((part, idx) => {
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^\)]+)\)$/);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      return (
        <a key={idx} href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:underline">
          {label}
        </a>
      );
    }
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={idx} className="text-white">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={idx}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith("_") && part.endsWith("_")) {
      return <em key={idx}>{part.slice(1, -1)}</em>;
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

function isNoiseBlock(block = "") {
  const b = String(block || "").trim().toLowerCase();
  if (!b) return true;
  if (b.includes("font-family") || b.includes("background:") || b.includes("display:")) return true;
  if (/^[.#@][a-z0-9_-]+\s*\{/.test(b)) return true;
  if (/^(var|const|let)\s+[a-z0-9_$]+\s*=/.test(b)) return true;
  if (b === "{" || b === "}" || b === "};") return true;
  return false;
}

function renderMarkdown(content = "") {
  const normalized = String(content)
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/:::\s*(callout|cta|cycle|tips|stats)\s*/gi, "\n\n:::$1\n")
    .replace(/\n?:::\s*\n?/g, "\n:::\n")
    // Recover flattened markdown tables (common after HTML import via turndown)
    .replace(/\s+(\|\s*[^|\n]+\s*\|\s*[-:|\s]+\|)/g, "\n\n$1")
    .replace(/(\|\s*[-:|\s]+\|)\s+(\|\s*[^|\n]+\s*\|\s*[^|\n]+\s*\|)/g, "$1\n$2")
    .replace(/(\|\s*[^|\n]+\s*\|\s*[^|\n]+\s*\|)\s+(?=\|\s*[^|\n]+\s*\|\s*[^|\n]+\s*\|)/g, "$1\n")
    .replace(/\s+(#{1,6}\s+)/g, "\n\n$1")
    .replace(/(^|\n)\s*(#{1,6}\s+[^\n]+)/g, "\n\n$2\n\n")
    .replace(/(^|\s)_([^_\n]+)_($|\s)/g, "$1*$2*$3")
    .replace(/\n{3,}/g, "\n\n");

  const blocks = normalized
    .split(/\n\s*\n+/)
    .map((b) => b.trim())
    .filter((b) => b && !isNoiseBlock(b));

  return blocks.map((block, idx) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return null;

    const first = lines[0];

    if (/^:::\s*callout\b/i.test(first)) {
      const body = lines.filter((l, i) => i !== 0 && l !== ":::").join(" ").trim();
      if (!body) return null;
      return (
        <div key={idx} className="rounded-lg border border-white/10 bg-[#111118] p-5 md:p-6 text-white text-lg md:text-xl font-semibold leading-relaxed uppercase">
          {renderInline(body)}
        </div>
      );
    }

    if (/^:::\s*cta\b/i.test(first)) {
      const body = lines.filter((l, i) => i !== 0 && l !== ":::").join(" ").trim();
      if (!body) return null;
      return (
        <div key={idx} className="rounded-xl border border-red-400/30 bg-gradient-to-r from-[#1a050a] to-[#111118] p-5 md:p-6 text-center">
          <p className="text-white font-semibold text-lg">{renderInline(body)}</p>
        </div>
      );
    }

    if (/^:::\s*cycle\b/i.test(first)) {
      const items = lines
        .slice(1)
        .filter((l) => l && l !== ":::")
        .map((line) => {
          const [num = "", title = "", body = ""] = line.split("|").map((x) => x.trim());
          return { num, title, body };
        })
        .filter((x) => x.title || x.body);

      if (!items.length) return null;

      return (
        <div key={idx} className="grid gap-px rounded-lg border border-white/10 overflow-hidden md:grid-cols-4 bg-white/10">
          {items.map((item, i) => (
            <div key={i} className="bg-[#111118] p-4 md:p-5">
              <div className="text-4xl font-black text-white/10 leading-none">{item.num || String(i + 1).padStart(2, "0")}</div>
              <div className="text-white uppercase font-bold tracking-wider mt-2">{item.title}</div>
              <div className="text-slate-400 text-sm mt-2 leading-6">{item.body}</div>
            </div>
          ))}
        </div>
      );
    }

    if (/^:::\s*tips\b/i.test(first)) {
      const items = lines
        .slice(1)
        .filter((l) => l && l !== ":::")
        .map((line) => {
          const [icon = "•", title = "", body = ""] = line.split("|").map((x) => x.trim());
          return { icon, title, body };
        })
        .filter((x) => x.title || x.body);

      if (!items.length) return null;

      return (
        <div key={idx} className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="rounded-md border border-white/10 bg-[#111118] px-4 py-4 flex gap-3">
              <div className="text-lg leading-none mt-0.5">{item.icon}</div>
              <div>
                <div className="text-white font-semibold">{item.title}</div>
                {item.body ? <div className="text-slate-400 text-sm mt-1">{item.body}</div> : null}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (/^:::\s*stats\b/i.test(first)) {
      const rows = lines.slice(1).filter((l) => l && l !== ":::");
      const title = rows[0] || "Datos clave";
      const items = rows.slice(1).map((row) => {
        const [num = "", desc = ""] = row.split("|").map((x) => x.trim());
        return { num, desc };
      }).filter((x) => x.num || x.desc);

      if (!items.length) return null;

      return (
        <div key={idx} className="rounded-lg border border-white/10 bg-[#111118] p-5 md:p-6">
          <div className="text-red-400 uppercase tracking-[0.14em] text-xs font-semibold mb-4">{title}</div>
          <div className="grid md:grid-cols-3 gap-5">
            {items.map((item, i) => (
              <div key={i}>
                <div className="text-3xl md:text-4xl font-black text-white leading-none">{item.num}</div>
                {item.desc ? <div className="text-slate-400 text-sm mt-2 leading-6">{item.desc}</div> : null}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (/^###\s+/.test(first)) {
      const rest = lines.slice(1).join("\n").trim();
      return (
        <React.Fragment key={idx}>
          <h3 className="text-xl md:text-2xl font-bold mt-8 text-white tracking-wide uppercase">
            {renderInline(first.replace(/^###\s+/, ""))}
          </h3>
          {rest ? renderMarkdown(rest) : null}
        </React.Fragment>
      );
    }

    if (/^##\s+/.test(first)) {
      const rest = lines.slice(1).join("\n").trim();
      return (
        <React.Fragment key={idx}>
          <h2 className="text-2xl md:text-3xl font-extrabold mt-10 text-white tracking-wide uppercase border-l-4 border-red-500 pl-3">
            {renderInline(first.replace(/^##\s+/, ""))}
          </h2>
          {rest ? renderMarkdown(rest) : null}
        </React.Fragment>
      );
    }

    if (/^#\s+/.test(first)) {
      const rest = lines.slice(1).join("\n").trim();
      return (
        <React.Fragment key={idx}>
          <h1 className="text-3xl md:text-4xl font-black mt-10 text-white tracking-wide uppercase">
            {renderInline(first.replace(/^#\s+/, ""))}
          </h1>
          {rest ? renderMarkdown(rest) : null}
        </React.Fragment>
      );
    }

    if (/^>\s+/.test(first)) {
      const quote = lines.map((l) => l.replace(/^>\s?/, "")).join(" ");
      return (
        <blockquote key={idx} className="rounded-lg border border-white/10 bg-[#111118] p-5 md:p-6 text-white text-lg md:text-xl font-semibold leading-relaxed uppercase">
          {renderInline(quote)}
        </blockquote>
      );
    }

    const isMarkdownTable =
      lines.length >= 2 &&
      lines[0].includes("|") &&
      /^\|?\s*[:\-\|\s]+\|?\s*$/.test(lines[1]);

    if (isMarkdownTable) {
      const splitRow = (row) =>
        row
          .split("|")
          .map((c) => c.trim())
          .filter((c, i, arr) => !(i === 0 && c === "") && !(i === arr.length - 1 && c === ""));

      const headers = splitRow(lines[0]);
      const bodyRows = lines.slice(2).map(splitRow).filter((r) => r.length);

      return (
        <div key={idx} className="overflow-x-auto rounded-lg border border-white/15">
          <table className="w-full text-sm">
            <thead className="bg-white/10 text-white">
              <tr>
                {headers.map((h, hIdx) => (
                  <th key={hIdx} className="text-left px-3 py-2 border-b border-white/20 whitespace-nowrap">{renderInline(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rIdx) => (
                <tr key={rIdx} className="border-b border-white/10 last:border-b-0">
                  {row.map((cell, cIdx) => (
                    <td key={cIdx} className="px-3 py-2 align-top text-slate-200">{renderInline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    const isBulletList = lines.every((l) => /^[-*]\s+/.test(l));
    if (isBulletList) {
      return (
        <ul key={idx} className="space-y-3">
          {lines.map((line, liIdx) => (
            <li key={liIdx} className="rounded-md border border-white/10 bg-[#111118] px-4 py-3 text-slate-200">
              <span className="text-red-400 mr-2">•</span>
              {renderInline(line.replace(/^[-*]\s+/, ""))}
            </li>
          ))}
        </ul>
      );
    }

    const isNumberedList = lines.every((l) => /^\d+\.\s+/.test(l));
    if (isNumberedList) {
      return (
        <ol key={idx} className="list-decimal pl-6 space-y-2 text-slate-200">
          {lines.map((line, liIdx) => (
            <li key={liIdx}>{renderInline(line.replace(/^\d+\.\s+/, ""))}</li>
          ))}
        </ol>
      );
    }

    if (/^---+$/.test(first)) {
      return <hr key={idx} className="border-white/10 my-8" />;
    }

    // Recover inline/flattened markdown tables embedded inside a paragraph block
    const compact = lines.join(" ");
    const inlineTableMatch = compact.match(/^(.*?)(\|\s*[^|]+?\s*\|\s*[^|]+?\s*\|\s*\|\s*[:\-\|\s]+\|\s*(?:\|\s*[^|]+?\s*\|\s*[^|]+?\s*\|\s*)+)(.*)$/);
    if (inlineTableMatch) {
      const before = inlineTableMatch[1]?.trim();
      const tableText = inlineTableMatch[2] || "";
      const after = inlineTableMatch[3]?.trim();

      const tuples = Array.from(tableText.matchAll(/\|\s*([^|]+?)\s*\|\s*([^|]+?)\s*\|/g)).map((m) => [m[1].trim(), m[2].trim()]);
      const clean = tuples.filter(([a, b]) => !/^[:\-\s]+$/.test(a) && !/^[:\-\s]+$/.test(b));
      const headers = clean[0] || [];
      const rows = clean.slice(1);

      return (
        <React.Fragment key={idx}>
          {before ? <p className="text-slate-200 leading-8">{renderInline(before)}</p> : null}
          {headers.length ? (
            <div className="overflow-x-auto rounded-lg border border-white/15">
              <table className="w-full text-sm">
                <thead className="bg-white/10 text-white">
                  <tr>
                    {headers.map((h, hIdx) => (
                      <th key={hIdx} className="text-left px-3 py-2 border-b border-white/20 whitespace-nowrap">{renderInline(h)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rIdx) => (
                    <tr key={rIdx} className="border-b border-white/10 last:border-b-0">
                      {row.map((cell, cIdx) => (
                        <td key={cIdx} className="px-3 py-2 align-top text-slate-200">{renderInline(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {after ? <p className="text-slate-200 leading-8">{renderInline(after)}</p> : null}
        </React.Fragment>
      );
    }

    return <p key={idx} className="text-slate-200 leading-8">{renderInline(lines.join(" "))}</p>;
  });
}

const ENABLE_LOCAL_BLOG_FALLBACK = false;

export default function BlogPostPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const slug = window.location.pathname.replace("/blog/", "");
  const [post, setPost] = useState(ENABLE_LOCAL_BLOG_FALLBACK ? (BLOG_POSTS.find((p) => p.slug === slug) || null) : null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!hasSupabaseConfig || !supabase) {
        if (!ENABLE_LOCAL_BLOG_FALLBACK && !cancelled) setPost(null);
        return;
      }

      const { data, error } = await supabase
        .from("blog_posts")
        .select("slug,title,excerpt,content_md,cover_image_url,category,author_name,published_at,created_at,status")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();

      if (!cancelled && !error && data) {
        setPost(mapDbPostToUi(data));
      } else if (!cancelled && !ENABLE_LOCAL_BLOG_FALLBACK) {
        setPost(null);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const markdownContent = useMemo(() => {
    if (!post) return "";
    if (post.contentMd) return post.contentMd;
    if (Array.isArray(post.content)) return post.content.join("\n\n");
    return "";
  }, [post]);

  useEffect(() => {
    if (!post) return;

    document.title = `${post.title} | Blog PREL`;

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    const url = `https://www.puertoricoesportleague.com/blog/${post.slug}`;
    setMeta("description", post.excerpt || "Artículo del blog de Puerto Rico Esports League.");
    setMeta("og:title", `${post.title} | Blog PREL`, true);
    setMeta("og:description", post.excerpt || "Artículo del blog de Puerto Rico Esports League.", true);
    setMeta("og:type", "article", true);
    setMeta("og:url", url, true);
    setMeta("og:image", post.cover?.startsWith("http") ? post.cover : `https://www.puertoricoesportleague.com${post.cover}`, true);
    setMeta("twitter:card", "summary_large_image");

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#080A10] text-white">
        <SiteHeader current="/blog" />
        <main className="px-6 md:px-10 pt-28 md:pt-36 pb-16">
          <div className="max-w-3xl mx-auto rounded-2xl border border-white/10 bg-[#141828] p-6 md:p-8">
            <a href="/blog" className="text-cyan-300 text-sm hover:underline">{lang === "en" ? "← Back to blog" : "← Volver al blog"}</a>
            <h1 className="text-3xl font-bold mt-4 uppercase">{lang === "en" ? "Article not found" : "Artículo no encontrado"}</h1>
            <p className="mt-3 text-slate-300">{lang === "en" ? "The link may have changed or the post may no longer be available." : "Puede que el enlace haya cambiado o que la publicación ya no esté disponible."}</p>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080A10] text-white">
      <SiteHeader current="/blog" />

      <main className="relative overflow-hidden px-6 md:px-10 pt-28 md:pt-36 pb-16">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_70%_at_92%_8%,rgba(0,42,143,0.16)_0%,transparent_60%),radial-gradient(ellipse_38%_55%_at_6%_16%,rgba(206,17,38,0.12)_0%,transparent_60%)] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto">
          <a href="/blog" className="text-cyan-300 text-sm hover:underline">{lang === "en" ? "← Back to blog" : "← Volver al blog"}</a>

          <div className="mt-6 relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#141420] to-[#0A0A0F] p-6 md:p-10">
            <div className="absolute -top-24 -left-24 w-64 h-64 bg-red-600/20 blur-3xl rounded-full pointer-events-none" />

            <p className="text-xs uppercase tracking-[0.18em] text-red-400 font-semibold">{post.category || "Blog"}</p>
            <h1 className="mt-4 text-3xl md:text-5xl font-black leading-tight uppercase">
              {post.title}
            </h1>
            <p className="text-slate-400 text-xs md:text-sm mt-4 uppercase tracking-wider">
              {post.author} · {new Date(post.date).toLocaleDateString()}
            </p>

            {post.excerpt ? <p className="text-slate-300 mt-5 max-w-3xl">{post.excerpt}</p> : null}
          </div>

          <img
            src={post.cover}
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-2xl mt-6 border border-white/10"
          />

          <article className="mt-8 space-y-5 text-[17px]">
            {renderMarkdown(markdownContent)}
          </article>

          <div className="mt-10 rounded-2xl border border-red-400/30 bg-gradient-to-r from-[#1a050a] to-[#111118] p-6 text-center">
            <h3 className="text-2xl md:text-3xl font-black uppercase">{lang === "en" ? "Ready to compete?" : "¿Listo para competir?"}</h3>
            <p className="text-slate-300 mt-2">{lang === "en" ? "Register for upcoming events and secure your place in the league." : "Regístrate para próximos eventos y asegura tu espacio en la liga."}</p>
            <a href="/#eventos" className="inline-block mt-4 px-5 py-3 rounded-xl bg-white text-black font-semibold">{lang === "en" ? "Events" : "Eventos"}</a>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
